<template>
  <div class="inventory-issue-resolution">
    <v-breadcrumbs>
      <v-breadcrumbs-item :exact="true" :to="{ name: 'home', params: {} }">
        Home
      </v-breadcrumbs-item>
      <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
      <v-breadcrumbs-item
        :exact="true"
        :to="{ name: 'inventoryReservation', params: {} }"
      >
        Inventory Reservations
      </v-breadcrumbs-item>
      <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
      <v-breadcrumbs-item active>
        Issues
      </v-breadcrumbs-item>
    </v-breadcrumbs>

    <v-tabs v-model="activeTab" background-color="transparent" grow>
      <v-tab>
        <v-icon left color="warning">mdi-alert-circle-outline</v-icon>
        Needs Review
        <v-badge
          v-if="needsReviewItems.length > 0"
          :content="needsReviewItems.length"
          color="warning"
          inline
          class="ml-2"
        />
      </v-tab>
      <v-tab>
        <v-icon left color="info">mdi-clock-outline</v-icon>
        Backorder
        <v-badge
          v-if="backorderItems.length > 0"
          :content="backorderItems.length"
          color="info"
          inline
          class="ml-2"
        />
      </v-tab>
      <v-tab>
        <v-icon left color="success">mdi-check-circle-outline</v-icon>
        Recently Resolved
        <v-badge
          v-if="recentlyResolved.length > 0"
          :content="recentlyResolved.length"
          color="success"
          inline
          class="ml-2"
        />
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab">
      <!-- NEEDS_REVIEW Tab -->
      <v-tab-item>
        <v-progress-linear
          v-if="!issuesProcessed"
          indeterminate
          color="warning"
        />
        <div v-else-if="needsReviewItems.length === 0" class="pa-6 text-center">
          <v-icon large color="grey lighten-1">mdi-check-all</v-icon>
          <div class="text-h6 grey--text mt-2">
            No issues requiring review
          </div>
        </div>
        <v-list v-else three-line class="pa-2">
          <issue-card
            v-for="issue in needsReviewItems"
            :key="issue.id"
            :issue="issue"
            :resolving-id="resolvingId"
            :resolution-notes="resolutionNotes"
            @update:note="updateNote(issue.id, $event)"
            @resolve-with-location="confirmResolveWithLocation(issue)"
            @resolve-manually="confirmResolveManually(issue)"
            @resolve-with-surplus="confirmResolveWithSurplus(issue)"
            @mark-backorder="confirmMarkBackorder(issue)"
          />
        </v-list>
      </v-tab-item>

      <!-- BACKORDER Tab -->
      <v-tab-item>
        <v-progress-linear
          v-if="!issuesProcessed"
          indeterminate
          color="info"
        />
        <div v-else-if="backorderItems.length === 0" class="pa-6 text-center">
          <v-icon large color="grey lighten-1">mdi-package-variant</v-icon>
          <div class="text-h6 grey--text mt-2">
            No backorder items
          </div>
        </div>
        <v-list v-else three-line class="pa-2">
          <issue-card
            v-for="issue in backorderItems"
            :key="issue.id"
            :issue="issue"
            :resolving-id="resolvingId"
            :resolution-notes="resolutionNotes"
            @update:note="updateNote(issue.id, $event)"
            @resolve-with-location="confirmResolveWithLocation(issue)"
            @resolve-manually="confirmResolveManually(issue)"
            @resolve-with-surplus="confirmResolveWithSurplus(issue)"
            @mark-backorder="confirmMarkBackorder(issue)"
          />
        </v-list>
      </v-tab-item>

      <!-- Recently Resolved Tab -->
      <v-tab-item>
        <v-progress-linear
          v-if="loadingResolved"
          indeterminate
          color="success"
        />
        <div
          v-else-if="recentlyResolved.length === 0"
          class="pa-6 text-center"
        >
          <v-icon large color="grey lighten-1">mdi-history</v-icon>
          <div class="text-h6 grey--text mt-2">
            No recently resolved issues in the last 24 hours
          </div>
        </div>
        <v-list v-else three-line class="pa-2">
          <v-card
            v-for="item in recentlyResolved"
            :key="item.id"
            outlined
            class="mb-3"
          >
            <v-card-title class="pb-1">
              <a
                class="issue-link"
                @click="openWorksheet(item.processWorksheetId)"
              >
                Worksheet #{{ item.worksheetId || item.processWorksheetId }}
              </a>
              <v-spacer />
              <v-chip small color="success" text-color="white">
                Resolved
              </v-chip>
            </v-card-title>
            <v-card-subtitle>
              {{ item.productName }}
            </v-card-subtitle>
            <v-card-text>
              <div class="d-flex flex-wrap ga-2 mb-2">
                <v-chip x-small outlined v-if="item.processName">
                  {{ item.processName }}
                </v-chip>
                <v-chip x-small outlined v-if="item.locationName">
                  <v-icon x-small left>mdi-map-marker</v-icon>
                  {{ item.locationName }}
                </v-chip>
              </div>
              <div class="text-caption grey--text">
                <span v-if="item.staffName">{{ item.staffName }}</span>
                <span v-if="item.inventoryDecisionTime">
                  &mdash; Resolved {{ item.inventoryDecisionTime | formatDateTimeYear }}
                </span>
              </div>
              <div
                v-if="item.inventoryDecisionNote"
                class="text-caption mt-1"
              >
                {{ item.inventoryDecisionNote }}
              </div>
            </v-card-text>
          </v-card>
        </v-list>
      </v-tab-item>
    </v-tabs-items>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="confirmDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h6">
          {{ confirmTitle }}
        </v-card-title>
        <v-card-text>
          {{ confirmMessage }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelConfirm">
            Cancel
          </v-btn>
          <v-btn
            :color="confirmColor"
            :loading="resolvingId !== null"
            @click="executeConfirmedAction"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="4000"
      top
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";

const IssueCard = {
  name: "IssueCard",
  props: {
    issue: { type: Object, required: true },
    resolvingId: { default: null },
    resolutionNotes: { type: Object, required: true },
  },
  computed: {
    shortageInfo() {
      return this.parseShortageNote(this.issue.inventoryDecisionNote);
    },
    age() {
      if (!this.issue.createTime) return "";
      const now = new Date();
      const created = new Date(this.issue.createTime);
      const diffMs = now - created;
      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 60) return diffMins + " minutes ago";
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return diffHours + " hours ago";
      const diffDays = Math.floor(diffHours / 24);
      return diffDays + (diffDays === 1 ? " day ago" : " days ago");
    },
    selectionMethodLabel() {
      const method = this.issue.processInputMethod;
      if (!method) return null;
      const labels = { FIFO: "FIFO", LIFO: "LIFO", MANUAL: "Manual" };
      return labels[method] || method;
    },
    statusColor() {
      return this.issue.inventoryDecisionStatus === "NEEDS_REVIEW"
        ? "warning"
        : "info";
    },
    statusLabel() {
      return this.issue.inventoryDecisionStatus === "NEEDS_REVIEW"
        ? "Needs Review"
        : "Backorder";
    },
    isResolving() {
      return this.resolvingId === this.issue.id;
    },
    noteValue() {
      return this.resolutionNotes[this.issue.id] || "";
    },
  },
  methods: {
    parseShortageNote(note) {
      if (!note) return null;
      // Try to extract: "Available: X kg, Requested: Y kg, Shortfall: Z kg"
      const availableMatch = note.match(/[Aa]vailable\s*:?\s*([\d.,]+)\s*(kg)?/);
      const requestedMatch = note.match(/[Rr]equested\s*:?\s*([\d.,]+)\s*(kg)?/);
      const shortfallMatch = note.match(/[Ss]hortfall\s*:?\s*([\d.,]+)\s*(kg)?/);
      if (availableMatch || requestedMatch || shortfallMatch) {
        return {
          available: availableMatch ? availableMatch[1] : "?",
          requested: requestedMatch ? requestedMatch[1] : "?",
          shortfall: shortfallMatch ? shortfallMatch[1] : "?",
        };
      }
      return null;
    },
    updateNote(value) {
      this.$emit("update:note", value);
    },
  },
  template: `
    <v-card outlined class="mb-3">
      <v-card-title class="pb-1">
        <a
          class="issue-link"
          @click="$router.push('/processWorksheet/view/' + issue.processWorksheetId)"
        >
          Worksheet #{{ issue.worksheetId || issue.processWorksheetId }}
        </a>
        <v-spacer />
        <v-chip small :color="statusColor" text-color="white">
          {{ statusLabel }}
        </v-chip>
      </v-card-title>

      <v-card-subtitle>
        {{ issue.productName }}
      </v-card-subtitle>

      <v-card-text>
        <div class="d-flex flex-wrap mb-2" style="gap: 6px;">
          <v-chip x-small outlined v-if="issue.processName">
            {{ issue.processName }}
          </v-chip>
          <v-chip x-small outlined v-if="issue.locationName">
            <v-icon x-small left>mdi-map-marker</v-icon>
            {{ issue.locationName }}
          </v-chip>
          <v-chip x-small outlined v-if="selectionMethodLabel">
            <v-icon x-small left>mdi-sort</v-icon>
            {{ selectionMethodLabel }}
          </v-chip>
        </div>

        <div v-if="shortageInfo" class="shortage-info mb-2">
          <v-icon small color="orange" class="mr-1">mdi-scale-balance</v-icon>
          <span class="text-body-2">
            Available: <strong>{{ shortageInfo.available }} kg</strong>,
            Requested: <strong>{{ shortageInfo.requested }} kg</strong>,
            Shortfall: <strong class="red--text">{{ shortageInfo.shortfall }} kg</strong>
          </span>
        </div>
        <div v-else-if="issue.inventoryDecisionNote" class="text-body-2 mb-2">
          {{ issue.inventoryDecisionNote }}
        </div>

        <div class="d-flex align-center text-caption grey--text">
          <v-icon x-small class="mr-1">mdi-account</v-icon>
          <span v-if="issue.staffName">{{ issue.staffName }}</span>
          <span v-if="issue.createTime" class="ml-2">
            <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
            {{ age }}
          </span>
        </div>
      </v-card-text>

      <v-expansion-panels flat accordion class="issue-actions">
        <v-expansion-panel>
          <v-expansion-panel-header class="text-subtitle-2">
            <v-icon small class="mr-2">mdi-wrench</v-icon>
            Resolution Actions
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="d-flex flex-column" style="gap: 12px;">
              <!-- Resolve with location -->
              <div>
                <v-btn
                  small
                  color="primary"
                  outlined
                  block
                  :loading="isResolving"
                  @click="$emit('resolve-with-location', issue)"
                >
                  <v-icon small left>mdi-map-marker-check</v-icon>
                  Resolve with Location
                </v-btn>
                <div class="text-caption grey--text mt-1">
                  Re-attempt inventory selection from the assigned location.
                </div>
              </div>

              <!-- Resolve manually -->
              <div>
                <v-textarea
                  :value="noteValue"
                  @input="updateNote"
                  outlined
                  dense
                  rows="2"
                  label="Resolution note"
                  placeholder="Describe how this was resolved manually..."
                  hide-details="auto"
                  class="mb-1"
                />
                <v-btn
                  small
                  color="success"
                  outlined
                  block
                  :disabled="!noteValue || noteValue.trim().length === 0"
                  :loading="isResolving"
                  @click="$emit('resolve-manually', issue)"
                >
                  <v-icon small left>mdi-pencil-check</v-icon>
                  Resolve Manually
                </v-btn>
                <div class="text-caption grey--text mt-1">
                  Mark as resolved with a manual note explaining the resolution.
                </div>
              </div>

              <!-- Create surplus -->
              <div>
                <v-textarea
                  :value="noteValue"
                  @input="updateNote"
                  outlined
                  dense
                  rows="2"
                  label="Surplus note"
                  placeholder="Describe the surplus creation..."
                  hide-details="auto"
                  class="mb-1"
                />
                <v-btn
                  small
                  color="orange"
                  outlined
                  block
                  :disabled="!noteValue || noteValue.trim().length === 0"
                  :loading="isResolving"
                  @click="$emit('resolve-with-surplus', issue)"
                >
                  <v-icon small left>mdi-package-variant-plus</v-icon>
                  Create Surplus
                </v-btn>
                <div class="text-caption grey--text mt-1">
                  Create surplus inventory to cover the shortfall.
                </div>
              </div>

              <!-- Mark as backorder -->
              <div v-if="issue.inventoryDecisionStatus !== 'BACKORDER'">
                <v-btn
                  small
                  color="info"
                  outlined
                  block
                  :loading="isResolving"
                  @click="$emit('mark-backorder', issue)"
                >
                  <v-icon small left>mdi-clock-alert-outline</v-icon>
                  Mark as Backorder
                </v-btn>
                <div class="text-caption grey--text mt-1">
                  Move this issue to backorder status for later fulfillment.
                </div>
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card>
  `,
};

export default {
  name: "InventoryIssueResolution",
  components: {
    IssueCard,
  },
  data() {
    return {
      activeTab: 0,
      resolvingId: null,
      resolutionNotes: {},
      loadingResolved: false,
      snackbar: {
        show: false,
        text: "",
        color: "success",
      },
      confirmDialog: false,
      confirmTitle: "",
      confirmMessage: "",
      confirmColor: "primary",
      pendingAction: null,
    };
  },
  computed: {
    ...mapState("inventoryReservation", [
      "issues",
      "issuesProcessed",
      "recentlyResolved",
    ]),
    needsReviewItems() {
      return this.issues.filter(
        (i) => i.inventoryDecisionStatus === "NEEDS_REVIEW"
      );
    },
    backorderItems() {
      return this.issues.filter(
        (i) => i.inventoryDecisionStatus === "BACKORDER"
      );
    },
  },
  methods: {
    ...mapActions("inventoryReservation", [
      "fetchInventoryIssues",
      "fetchRecentlyResolved",
      "resolveIssue",
      "resolveIssueWithLocation",
      "resolveIssueWithSurplus",
      "markIssueAsBackorder",
    ]),
    ...mapActions("messages", [
      "addErrorMessage",
      "addSuccessMessage",
    ]),
    updateNote(issueId, value) {
      this.$set(this.resolutionNotes, issueId, value);
    },
    openWorksheet(worksheetId) {
      this.$router.push("/processWorksheet/view/" + worksheetId);
    },
    showSnackbar(text, color = "success") {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },

    // ---- Confirmation flow ----
    confirmResolveWithLocation(issue) {
      this.confirmTitle = "Resolve with Location";
      this.confirmMessage =
        'Re-attempt inventory selection from location "' +
        (issue.locationName || "ID: " + issue.locationId) +
        '" for Worksheet #' +
        (issue.worksheetId || issue.processWorksheetId) +
        "?";
      this.confirmColor = "primary";
      this.pendingAction = () => this.doResolveWithLocation(issue);
      this.confirmDialog = true;
    },
    confirmResolveManually(issue) {
      const note = (this.resolutionNotes[issue.id] || "").trim();
      if (!note) {
        this.showSnackbar("Please enter a resolution note.", "warning");
        return;
      }
      this.confirmTitle = "Resolve Manually";
      this.confirmMessage =
        "Mark Worksheet #" +
        (issue.worksheetId || issue.processWorksheetId) +
        ' as manually resolved with note: "' +
        note +
        '"?';
      this.confirmColor = "success";
      this.pendingAction = () => this.doResolveManually(issue);
      this.confirmDialog = true;
    },
    confirmResolveWithSurplus(issue) {
      const note = (this.resolutionNotes[issue.id] || "").trim();
      if (!note) {
        this.showSnackbar("Please enter a surplus note.", "warning");
        return;
      }
      this.confirmTitle = "Create Surplus";
      this.confirmMessage =
        "Create surplus inventory for Worksheet #" +
        (issue.worksheetId || issue.processWorksheetId) +
        ' with note: "' +
        note +
        '"?';
      this.confirmColor = "orange";
      this.pendingAction = () => this.doResolveWithSurplus(issue);
      this.confirmDialog = true;
    },
    confirmMarkBackorder(issue) {
      this.confirmTitle = "Mark as Backorder";
      this.confirmMessage =
        "Move Worksheet #" +
        (issue.worksheetId || issue.processWorksheetId) +
        " to backorder status?";
      this.confirmColor = "info";
      this.pendingAction = () => this.doMarkBackorder(issue);
      this.confirmDialog = true;
    },
    cancelConfirm() {
      this.confirmDialog = false;
      this.pendingAction = null;
    },
    async executeConfirmedAction() {
      if (this.pendingAction) {
        await this.pendingAction();
      }
      this.confirmDialog = false;
      this.pendingAction = null;
    },

    // ---- Action implementations ----
    async doResolveWithLocation(issue) {
      this.resolvingId = issue.id;
      try {
        await this.resolveIssueWithLocation({
          entryId: issue.id,
          locationId: issue.locationId,
        });
        this.showSnackbar("Issue resolved with location successfully.");
        await this.reloadIssues();
      } catch (error) {
        this.showSnackbar(
          "Failed to resolve with location: " + (error.message || "Server error"),
          "error"
        );
        this.addErrorMessage("Failed to resolve issue with location.");
      } finally {
        this.resolvingId = null;
      }
    },
    async doResolveManually(issue) {
      const note = (this.resolutionNotes[issue.id] || "").trim();
      this.resolvingId = issue.id;
      try {
        await this.resolveIssue({ entryId: issue.id, note });
        this.showSnackbar("Issue resolved manually.");
        this.$delete(this.resolutionNotes, issue.id);
        await this.reloadIssues();
      } catch (error) {
        this.showSnackbar(
          "Failed to resolve issue: " + (error.message || "Server error"),
          "error"
        );
        this.addErrorMessage("Failed to resolve issue manually.");
      } finally {
        this.resolvingId = null;
      }
    },
    async doResolveWithSurplus(issue) {
      const note = (this.resolutionNotes[issue.id] || "").trim();
      this.resolvingId = issue.id;
      try {
        await this.resolveIssueWithSurplus({ entryId: issue.id, note });
        this.showSnackbar("Surplus created and issue resolved.");
        this.$delete(this.resolutionNotes, issue.id);
        await this.reloadIssues();
      } catch (error) {
        this.showSnackbar(
          "Failed to create surplus: " + (error.message || "Server error"),
          "error"
        );
        this.addErrorMessage("Failed to resolve issue with surplus.");
      } finally {
        this.resolvingId = null;
      }
    },
    async doMarkBackorder(issue) {
      this.resolvingId = issue.id;
      try {
        await this.markIssueAsBackorder(issue.id);
        this.showSnackbar("Issue marked as backorder.");
        await this.reloadIssues();
      } catch (error) {
        this.showSnackbar(
          "Failed to mark as backorder: " + (error.message || "Server error"),
          "error"
        );
        this.addErrorMessage("Failed to mark issue as backorder.");
      } finally {
        this.resolvingId = null;
      }
    },
    async reloadIssues() {
      try {
        await this.fetchInventoryIssues();
        await this.fetchRecentlyResolved();
      } catch (error) {
        this.addErrorMessage("Failed to reload issues.");
      }
    },
  },
  watch: {
    activeTab(newVal) {
      // Reload recently resolved when that tab is selected
      if (newVal === 2) {
        this.loadingResolved = true;
        this.fetchRecentlyResolved()
          .catch(() => {
            this.addErrorMessage("Failed to load recently resolved issues.");
          })
          .finally(() => {
            this.loadingResolved = false;
          });
      }
    },
  },
  created() {
    this.fetchInventoryIssues().catch(() => {
      this.addErrorMessage("Failed to load inventory issues.");
    });
    this.fetchRecentlyResolved().catch(() => {
      // Silently fail on initial load; user can switch tabs to retry
    });
    this.$emit("showParent", false);
  },
};
</script>

<style scoped>
.issue-link {
  color: blue !important;
  cursor: pointer;
  text-decoration: none;
}

.issue-link:hover {
  text-decoration: underline;
}

.shortage-info {
  display: flex;
  align-items: center;
  background-color: #fff8e1;
  border-radius: 4px;
  padding: 6px 10px;
}

.issue-actions .v-expansion-panel-header {
  min-height: 40px;
  padding: 8px 16px;
}

.issue-actions .v-expansion-panel-content__wrap {
  padding: 8px 16px 16px;
}
</style>
